import { designTokens } from '@nha/design-tokens';
import { TECH_SUPPORT_CENTER_MAILTO, TECH_SUPPORT_CENTER_NAME } from '../constants/support-contact.constants';
import { UserSessionService } from '../services/user-session.service';
import { UserProfileLoadError } from '../types/user-profile-load-error.type';

export interface BootstrapErrorOptions {
	/** Overrides the default host (the pre-bootstrap loader's parent element, else document.body). */
	hostSelector?: string;
	/** Overrides the default sign-out (a fresh UserSessionService's logout). */
	onSignOut?: () => void;
}

interface BootstrapErrorCopy {
	title: string;
	body: string;
	reference: string;
	graphic: 'missing-record' | 'error';
}

export function describeBootstrapError(error: unknown): BootstrapErrorCopy {
	const reference = error instanceof Error ? error.message : String(error ?? 'Unknown error');

	if (error instanceof UserProfileLoadError) {
		if (error.isMissingStaffRecord) {
			return {
				title: "Sorry, your account isn't linked to a staff record yet!",
				body:
					'Your login worked, but myNHA could not find a staff record for your account. ' +
					'This usually happens when an account is new or was recently changed and the staff record has not been created yet.',
				reference,
				graphic: 'missing-record'
			};
		}

		return {
			title: "Sorry, we couldn't load your myNHA profile!",
			body:
				error.status === 0
					? 'myNHA could not reach the Account service. Check your connection and try again.'
					: `The Account service responded with an error (HTTP ${error.status}).`,
			reference,
			graphic: 'error'
		};
	}

	return {
		title: 'Sorry, something went wrong while starting myNHA!',
		body: 'The page could not finish loading. Try again, or sign out and back in.',
		reference,
		graphic: 'error'
	};
}

/**
 * Unique to this page but drawn in the app suite's empty-state language (pastel circle, dashed
 * cards, ${designTokens.color.gray[400]} strokes, floating accent chips): a staff ID badge on a lanyard. The
 * missing-record variant is a dashed "not created yet" ghost badge; the error variant is a solid
 * badge with the suite's red alert chip and emphasis dashes.
 */
// Colors are baked in from the token constants rather than read via var(--nha-*): this page is what
// the user sees when bootstrap has already failed, so it must not assume the app stylesheet applied.
// #E85656 is the splash page's alert red (libs/state/document-loading); it has no token yet.
const GRAPHICS: Record<BootstrapErrorCopy['graphic'], string> = {
	'missing-record': `
<svg data-graphic="missing-record" aria-hidden="true" style="width: 230px; height: auto" width="243" height="243" viewBox="0 0 243 243" fill="none" xmlns="http://www.w3.org/2000/svg">
	<path opacity="0.2" fill-rule="evenodd" clip-rule="evenodd" d="M121.5 243C188.603 243 243 188.603 243 121.5C243 54.3974 188.603 0 121.5 0C54.3974 0 0 54.3974 0 121.5C0 188.603 54.3974 243 121.5 243Z" fill="${designTokens.color.blue[300]}"/>
	<path d="M106 6C111 22 117 36 120 50" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.4" stroke-width="2" stroke-linecap="round"/>
	<path d="M138 6C133 22 127 36 124 50" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.4" stroke-width="2" stroke-linecap="round"/>
	<rect x="114" y="48" width="16" height="10" rx="2" fill="${designTokens.color.blue[200]}" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M122 58V64" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.6" stroke-width="2" stroke-linecap="round"/>
	<rect x="83" y="64" width="78" height="104" rx="5" fill="white" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="6 6"/>
	<circle cx="122" cy="100" r="17" fill="${designTokens.color.blue[25]}" stroke="${designTokens.color.gray[400]}" stroke-opacity="0.4" stroke-width="2" stroke-dasharray="6 6"/>
	<circle cx="116" cy="97" r="2" fill="${designTokens.color.gray[400]}" fill-opacity="0.6"/>
	<circle cx="128" cy="97" r="2" fill="${designTokens.color.gray[400]}" fill-opacity="0.6"/>
	<path opacity="0.6" d="M115 104C119 108 125 108 129 104" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M98 131.5H146" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M103 141.5H141" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path opacity="0.7" d="M110 151.5H134" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path fill-rule="evenodd" clip-rule="evenodd" d="M122 195C149.614 195 172 193.657 172 192C172 190.343 149.614 189 122 189C94.3858 189 72 190.343 72 192C72 193.657 94.3858 195 122 195Z" fill="${designTokens.color.gray[200]}" style="mix-blend-mode:multiply"/>
	<path d="M12.9927 119.5H30.0237" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M208.993 103.5H226.024" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M183.993 158.5H201.024" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M18.5747 128.5H28.4348" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<rect x="35" y="114" width="18" height="18" rx="5" fill="${designTokens.color.blue[300]}" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="120 6 80 6"/>
	<rect x="189" y="98" width="12" height="12" rx="5" fill="${designTokens.color.yellow[200]}" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="120 6 80 6"/>
</svg>`,
	'error': `
<svg data-graphic="error" aria-hidden="true" style="width: 230px; height: auto" width="243" height="243" viewBox="0 0 243 243" fill="none" xmlns="http://www.w3.org/2000/svg">
	<path opacity="0.2" fill-rule="evenodd" clip-rule="evenodd" d="M121.5 243C188.603 243 243 188.603 243 121.5C243 54.3974 188.603 0 121.5 0C54.3974 0 0 54.3974 0 121.5C0 188.603 54.3974 243 121.5 243Z" fill="#E85656"/>
	<path d="M106 6C111 22 117 36 120 50" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round"/>
	<path d="M138 6C133 22 127 36 124 50" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round"/>
	<rect x="114" y="48" width="16" height="10" rx="2" fill="${designTokens.color.blue[200]}" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M122 58V64" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round"/>
	<rect x="83" y="64" width="78" height="104" rx="5" fill="white" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="120 6 80 6"/>
	<circle cx="122" cy="100" r="17" fill="${designTokens.color.gray[50]}" stroke="${designTokens.color.gray[400]}" stroke-width="2"/>
	<circle cx="116" cy="97" r="2" fill="${designTokens.color.gray[400]}"/>
	<circle cx="128" cy="97" r="2" fill="${designTokens.color.gray[400]}"/>
	<path d="M115 108C119 104 125 104 129 107" stroke="${designTokens.color.gray[400]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M98 131.5H146" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M103 141.5H141" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path opacity="0.7" d="M110 151.5H134" stroke="${designTokens.color.gray[200]}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<rect x="76" y="69" width="22" height="22" rx="3" fill="#E85656" fill-opacity="0.2"/>
	<rect x="73" y="66" width="22" height="22" rx="3" fill="#E85656"/>
	<rect x="83" y="80" width="2" height="2" rx="1" fill="white"/>
	<rect x="83" y="73" width="2" height="6" rx="1" fill="white"/>
	<path d="M29.9638 113.5H61.0237" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M186.08 113.5H217.14" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M47.1219 100.023L63.8942 102.98" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M188.238 102.977L205.01 100.02" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M51.1218 127.977L67.8941 125.02" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path d="M181.238 125.023L198.01 127.98" stroke="#E85656" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
	<path fill-rule="evenodd" clip-rule="evenodd" d="M122 198C149.614 198 172 196.657 172 195C172 193.343 149.614 192 122 192C94.3858 192 72 193.343 72 195C72 196.657 94.3858 198 122 198Z" fill="#E85656" fill-opacity="0.5" style="mix-blend-mode:multiply"/>
</svg>`
};

/**
 * Replaces the pre-bootstrap loader with a plain-DOM, full-screen error page styled to match the
 * shared 403 page. Runs before (or instead of) Angular, so it cannot depend on anything
 * Angular-provided; RobotoBold/Roboto come from the global stylesheet index.html already links.
 */
export function renderBootstrapError(error: unknown, options: BootstrapErrorOptions = {}): void {
	const host: Element =
		(options.hostSelector ? document.querySelector(options.hostSelector) : document.querySelector('.nha-loader')?.parentElement) ?? document.body;
	const onSignOut = options.onSignOut ?? (() => new UserSessionService().logout());
	const copy = describeBootstrapError(error);
	const reference = `${copy.reference} (${new Date().toLocaleString()})`;

	host.innerHTML = '';

	const page = document.createElement('div');
	page.className = 'nha-bootstrap-error';
	page.setAttribute('role', 'alert');
	page.innerHTML = `
		<style>
			.nha-bootstrap-error { position: fixed; top: 0; right: 0; bottom: 0; left: 0; overflow: auto; text-align: center; background: ${designTokens.color.white}; color: ${designTokens.color.gray[900]}; padding: 50px; box-sizing: border-box; font: 16px Roboto, "Segoe UI", Helvetica, Arial, sans-serif; }
			.nha-bootstrap-error h1 { font: 44px RobotoBold, Roboto, "Segoe UI", Helvetica, Arial, sans-serif; font-weight: 700; margin: 24px 0 16px; }
			.nha-bootstrap-error__body { font-size: 16px; line-height: 1.5; margin: 0 auto; max-width: 720px; }
			.nha-bootstrap-error__actions { margin-top: 25px; display: flex; gap: 12px; justify-content: center; align-items: center; }
			.nha-bootstrap-error__actions a.primary, .nha-bootstrap-error__actions button { font: 16px RobotoMedium, Roboto, "Segoe UI", Helvetica, Arial, sans-serif; font-weight: 500; height: 40px; padding: 0 20px; border-radius: 4px; cursor: pointer; box-sizing: border-box; }
			/* Matches the themed mat-flat-button: --mat-sys-primary ($nhaBlue600), 4px shape, 40px height. */
			.nha-bootstrap-error__actions a.primary { display: inline-flex; align-items: center; background: ${designTokens.color.brand.primary}; color: ${designTokens.color.white}; border: 1px solid ${designTokens.color.brand.primary}; text-decoration: none; }
			.nha-bootstrap-error__actions button.secondary { background: ${designTokens.color.white}; color: ${designTokens.color.brand.primary}; border: 1px solid ${designTokens.color.brand.primary}; }
			.nha-bootstrap-error__reference { margin: 32px auto 0; max-width: 640px; font-size: 12px; color: ${designTokens.color.gray[600]}; }
			.nha-bootstrap-error__reference code { display: block; margin-top: 4px; padding: 8px 10px; background: ${designTokens.color.gray[50]}; border-radius: 6px; font-family: "SFMono-Regular", Consolas, Menlo, monospace; word-break: break-word; color: ${designTokens.color.gray[900]}; }
		</style>
		${GRAPHICS[copy.graphic]}
		<h1></h1>
		<div class="nha-bootstrap-error__body"></div>
		<div class="nha-bootstrap-error__actions">
			<a class="primary" data-action="contact">Contact ${TECH_SUPPORT_CENTER_NAME}</a>
			<button type="button" class="secondary" data-action="sign-out">Sign out</button>
		</div>
		<p class="nha-bootstrap-error__reference">Reference<code></code></p>`;

	// Text goes in via textContent: the reference echoes an API error message, never markup.
	page.querySelector('h1').textContent = copy.title;
	page.querySelector('.nha-bootstrap-error__body').textContent = copy.body;
	page.querySelector('.nha-bootstrap-error__reference code').textContent = reference;
	// The reference rides along in the email body, so support gets it even from users who
	// never read (or copy) the block below.
	page.querySelector('[data-action="contact"]').setAttribute('href', `${TECH_SUPPORT_CENTER_MAILTO}&body=${encodeURIComponent(`Reference: ${reference}`)}`);
	page.querySelector('[data-action="sign-out"]').addEventListener('click', () => onSignOut());

	host.appendChild(page);
}
